import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  Zap, 
  Flame,
  ArrowUpRight,
  Activity
} from 'lucide-react';
import type { Market } from '../types';
import { formatDistanceToNow, formatNumber } from '../utils/format';

interface MarketCardProps {
  market: Market;
  index?: number;
}

const categoryColors: Record<string, { bg: string; text: string; icon: string }> = {
  crypto: { bg: 'bg-blue-500/10', text: 'text-blue-400', icon: 'text-blue-500' },
  sports: { bg: 'bg-green-500/10', text: 'text-green-400', icon: 'text-green-500' },
  politics: { bg: 'bg-red-500/10', text: 'text-red-400', icon: 'text-red-500' },
  finance: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', icon: 'text-yellow-500' },
  ai: { bg: 'bg-purple-500/10', text: 'text-purple-400', icon: 'text-purple-500' },
  social: { bg: 'bg-pink-500/10', text: 'text-pink-400', icon: 'text-pink-500' },
  entertainment: { bg: 'bg-orange-500/10', text: 'text-orange-400', icon: 'text-orange-500' },
  science: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', icon: 'text-cyan-500' },
};

export function MarketCard({ market, index = 0 }: MarketCardProps) {
  const colors = categoryColors[market.category] || categoryColors.crypto;
  const yesOutcome = market.outcomes[0];
  const noOutcome = market.outcomes[1];
  
  const yesPercent = yesOutcome ? Math.round(yesOutcome.probability * 100) : 50;
  const noPercent = noOutcome ? Math.round(noOutcome.probability * 100) : 50;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ y: -4 }}
      className="group"
    >
      <Link to={`/markets/${market.id}`}>
        <div className="bg-omni-card border border-omni-border rounded-2xl overflow-hidden card-hover glow-primary">
          {/* Header */}
          <div className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                {/* Category Icon */}
                <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center`}>
                  <Activity className={`w-5 h-5 ${colors.icon}`} />
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
                    {market.category}
                  </span>
                  
                  {market.isTrending && (
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-orange-500/10 text-orange-400 flex items-center gap-1">
                      <Flame className="w-3 h-3" /> Trending
                    </span>
                  )}
                  
                  {market.isFeatured && (
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-purple-500/10 text-purple-400 flex items-center gap-1">
                      <Zap className="w-3 h-3" /> Featured
                    </span>
                  )}
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <ArrowUpRight className="w-5 h-5 text-gray-500" />
              </motion.div>
            </div>

            {/* Title */}
            <h3 className="mt-4 text-lg font-semibold text-white leading-snug group-hover:text-indigo-400 transition-colors">
              {market.title}
            </h3>
            
            {market.description && (
              <p className="mt-2 text-sm text-gray-400 line-clamp-2">
                {market.description}
              </p>
            )}
          </div>

          {/* Probability Bar */}
          <div className="px-5 py-3 bg-white/5">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-omni-success font-medium">{yesOutcome?.name || 'Yes'}</span>
              <span className="text-omni-danger font-medium">{noOutcome?.name || 'No'}</span>
            </div>
            
            <div className="relative h-2 bg-omni-border rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${yesPercent}%` }}
                transition={{ duration: 1, delay: 0.2 }}
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-omni-success to-green-400 rounded-full"
              />
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${noPercent}%` }}
                transition={{ duration: 1, delay: 0.3 }}
                className="absolute right-0 top-0 h-full bg-gradient-to-l from-omni-danger to-red-400 rounded-full"
              />
            </div>
            
            <div className="flex items-center justify-between mt-2">
              <span className="text-omni-success font-bold">{yesPercent}%</span>
              <span className="text-omni-danger font-bold">{noPercent}%</span>
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-4 flex items-center justify-between border-t border-omni-border">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-xs text-gray-500">Volume</p>
                <p className="text-sm font-semibold text-white">${formatNumber(market.totalVolume)}</p>
              </div>
              
              <div>
                <p className="text-xs text-gray-500">Liquidity</p>
                <p className="text-sm font-semibold text-white">${formatNumber(market.totalLiquidity)}</p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-gray-400">
              <Clock className="w-4 h-4" />
              <span className="text-sm">{formatDistanceToNow(market.endTime)}</span>
            </div>
          </div>

          {/* Quick Trade Buttons */}
          <div className="px-5 pb-5 grid grid-cols-2 gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center justify-center gap-2 py-2.5 bg-omni-success/10 hover:bg-omni-success/20 text-omni-success rounded-xl font-medium transition-colors"
            >
              <TrendingUp className="w-4 h-4" />
              Buy Yes
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center justify-center gap-2 py-2.5 bg-omni-danger/10 hover:bg-omni-danger/20 text-omni-danger rounded-xl font-medium transition-colors"
            >
              <TrendingDown className="w-4 h-4" />
              Buy No
            </motion.button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
